import jsep, { CoreExpression, Expression } from 'jsep';
import { getBaseExecutionContext } from './expressionFunctions';
import { SerializedNodes } from '@craftjs/core';

import _ from 'lodash';
import i18next from 'i18next';
import { binops, BinopsKey, unops, UnopsKey } from '../builder/layout/panels/helpers/expression/expression-helper';
import { IBaseScoreSection, BaseVariableSection, IBaseVariableSection } from 'src/services/api/api-client';

/*
 * Calculates simple expressions like `replaceValues(data['asd'], 'zxc', 'qwe')`.
 * `data` should be provided as a second argument.
 */
export const calculateSimpleExpressions = (
  scoreSection: IBaseScoreSection,
  answers: Record<string, any>,
  layout: SerializedNodes,
  variables: IBaseVariableSection[],
) => {
  const result = executeSimpleExpression(scoreSection.expression, answers, layout, variables);

  if (!isNaN(result) && scoreSection.decimal) return _.round(Number(result), scoreSection.decimal);

  return result;
};

export function executeSimpleExpression(
  expression: string,
  answers: Record<string, any>,
  layout: SerializedNodes,
  variables: IBaseVariableSection[],
): any {
  const executionContext = {
    ...getBaseExecutionContext(),
    answers,
    layout,
    variables,
  };

  const result = executeExpressionInner(expression, executionContext);
  if (!isNaN(result)) return result;

  throw new Error(i18next.t('GeneralFormBuilder.Panels.Score.ExpressionModal.Errors.WrongExpression'));
}

function executeExpressionInner(expression: string, data: any): any {
  const ast = jsep(expression);
  if (data === null) {
    return null;
  } else {
    const value = evaluateParsedExpression(ast, data);
    return value;
  }
}

function evaluateParsedExpression(expression: jsep.Expression, context: any) {
  return evaluate(toCoreExpression(expression), context);
}

function toCoreExpression(expression: jsep.Expression) {
  return expression as CoreExpression;
}

function evaluate(node: CoreExpression, context: any): any {
  switch (node.type) {
    case 'ArrayExpression':
      return evaluateArray(node.elements, context);

    case 'BinaryExpression':
      return binops[node.operator as BinopsKey](
        evaluate(toCoreExpression(node.left), context),
        evaluate(toCoreExpression(node.right), context),
      );

    case 'CallExpression':
      let caller;
      // TODO: uncomment if we need it
      // if (node.callee.type === 'MemberExpression') {
      //   assign = evaluateMember(node.callee as jsep.MemberExpression, context);
      //   caller = assign[0];
      //   fn = assign[1];
      // } else {
      const fn = evaluate(toCoreExpression(node.callee), context);

      // }
      if (typeof fn !== 'function') {
        return undefined;
      }
      const args = evaluateArray(node.arguments, context);
      return fn.apply(caller, [...args, context?.answers, context?.layout, context?.variables]);

    case 'ConditionalExpression':
      return evaluate(toCoreExpression(node.test), context)
        ? evaluate(toCoreExpression(node.consequent), context)
        : evaluate(toCoreExpression(node.alternate), context);

    case 'Identifier':
      return context[node.name];

    case 'Literal':
      return node.value;

    // TODO: uncomment if we need it
    //   case 'MemberExpression':
    //     return evaluateMember(node, context)[1];

    // TODO: uncomment if we need it
    //   case 'ThisExpression':
    //     return context;

    // TODO: uncomment if we need it
    //   case 'Compound':
    //     return context;

    case 'UnaryExpression':
      return unops[node.operator as UnopsKey](evaluate(toCoreExpression(node.argument), context));

    default:
      return;
  }
}

const evaluateArray = (list: (Expression | null)[], context: any) =>
  list.filter((x) => x !== null).map((v) => evaluate(toCoreExpression(v!), context));
